<template>
    <transition name="pt-fade-in">
        <div
            v-if="visible"
            :style="{
                right: styleRight,
                bottom: styleBottom,
            }"
            class="pt-backtop"
            @click.stop="handleClick"
        >
            <slot>
                <pt-icon icon="arrow-up" />
            </slot>
        </div>
    </transition>
</template>

<script lang="ts">
export default {
    name: 'PtBacktop',
};
</script>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { throttle } from 'lodash-es';
import { on, off } from '../../../utils/dom';
import { easeInOutCubic } from '../../../utils/animation';
import throwError from '../../../utils/error';
import PtIcon from '../../icon';

interface Props {
    visibilityHeight?: number;
    target?: string;
    right?: number;
    bottom?: number;
}

const props = withDefaults(defineProps<Props>(), {
    visibilityHeight: 200,
    right: 40,
    bottom: 40,
});

const emit = defineEmits(['click']);

let el: HTMLElement | undefined;
let container: Document | HTMLElement = document;
const visible = ref(false);
const styleBottom = computed(() => `${props.bottom}px`);
const styleRight = computed(() => `${props.right}px`);
const scope = 'PtBackTop';

const scrollToTop = () => {
    if (!el) return;
    const beginTime = Date.now();
    const beginValue = el.scrollTop;
    const rAF = window.requestAnimationFrame || ((func) => setTimeout(func, 16));
    const frameFunc = () => {
        if (!el) return;
        const progress = (Date.now() - beginTime) / 500;
        if (progress < 1) {
            el.scrollTop = beginValue * (1 - easeInOutCubic(progress));
            rAF(frameFunc);
        } else {
            el.scrollTop = 0;
        }
    };
    rAF(frameFunc);
};
const onScroll = () => {
    if (!el) return;
    visible.value = el.scrollTop >= props.visibilityHeight;
};
const handleClick = (event: MouseEvent) => {
    scrollToTop();
    emit('click', event);
};

const throttledScrollHandler = throttle(onScroll, 300);

onMounted(() => {
    container = document;
    el = document.documentElement;
    if (props.target) {
        el = document.querySelector(props.target) as HTMLElement | undefined;
        if (!el) return throwError(scope, `target is not existed: ${props.target}`);
        container = el;
    }
    on(container, 'scroll', throttledScrollHandler);
});
onBeforeUnmount(() => {
    off(container, 'scroll', throttledScrollHandler);
});
</script>

<style lang="scss" src="../../theme-chalk/src/backtop.scss" />
